import React, { useEffect, useState } from "react";
import { IoIosPersonAdd } from "react-icons/io";
import { Receipt, Calendar, ChevronDown, Building2 } from "lucide-react";
import { RotatingLines } from "react-loader-spinner";
import { useAuth } from "../../context/Auth";
import NewExpense from "./NewExpense";

const ExpenseTransaction = () => {
  const { requestWithAuth } = useAuth();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dateValue, setDateValue] = useState("");
  const [showDateRanges, setShowDateRanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("Consolidated");
  const [expenseModal, setExpenseModal] = useState(false);

  const getDateRange = (value) => {
    let start_date = "";
    let end_date = "";
    if (value === "Today") {
      setShowDateRanges(false);
      start_date = formatDate(new Date());
      end_date = formatDate(new Date());
    } else if (value === "Yesterday") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
    } else if (value === "Last 7 Days") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 7))
      );
      end_date = formatDate(new Date());
    } else if (value === "Last 30 Days") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 30))
      );
      end_date = formatDate(new Date());
    } else if (value === "This Month") {
      setShowDateRanges(false);
      start_date = formatDate(new Date(new Date().setDate(1)));
      end_date = formatDate(new Date());
    } else if (value === "Last Month") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setMonth(new Date().getMonth() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setMonth(new Date().getMonth() - 1))
      );
    } else if (value === "This Year") {
      setShowDateRanges(false);
      start_date = formatDate(new Date(new Date().setMonth(0)));
      end_date = formatDate(new Date());
    } else if (value === "Last Year") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setFullYear(new Date().getFullYear() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setFullYear(new Date().getFullYear() - 1))
      );
    } else if (value === "Custom") {
      setShowDateRanges(true);
    }
    setStartDate(start_date);
    setEndDate(end_date);
    const strDateValue = `${start_date} - ${end_date}`;
    setDateValue(strDateValue);
  };

  // FORMAT DATE
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
    }
  };

  const getBranchExpenses = async () => {
    window.location.href = `/transaction/expense/print?branch=${branch}&startDate=${startDate}&endDate=${endDate}`;
  };

  useEffect(() => {
    fetchBranches();
    getDateRange("Last 30 Days");
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {expenseModal && (
        <NewExpense modalOpen={expenseModal} setModalOpen={setExpenseModal} />
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Transactions</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Expenses</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Receipt size={32} className="text-blue-500" />
                Expense Transactions
              </h1>
            </div>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
              onClick={() => setExpenseModal(true)}
            >
              <IoIosPersonAdd className="text-lg" />
              New Expense
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Receipt className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Branch Expenses Report
                </h2>
                <p className="text-sm text-gray-500">
                  Generate expense reports by branch and date range
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
              <IoIosPersonAdd className="text-gray-400" />
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Expense Details
              </label>
            </div>

            <div className="space-y-6">
              {/* Branch Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Building2 size={14} className="inline mr-1" />
                  Branch:
                </label>
                <select
                  onChange={(e) => setBranch(e.target.value)}
                  value={branch}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                >
                  <option value="Consolidated">Consolidated</option>
                  {branches.map((branch, index) => (
                    <option key={index} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar size={14} className="inline mr-1" />
                  Date:
                </label>
                <div className="relative">
                  <input
                    value={dateValue}
                    onClick={() => setShowDropdown(!showDropdown)}
                    readOnly
                    placeholder="Select date range"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                  />
                  <ChevronDown
                    size={20}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />

                  {showDropdown && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                      <div className="py-1">
                        {[
                          "Today",
                          "Yesterday",
                          "Last 7 Days",
                          "Last 30 Days",
                          "This Month",
                          "Last Month",
                          "Custom",
                        ].map((option) => (
                          <div
                            key={option}
                            onClick={() => {
                              getDateRange(option);
                              setShowDropdown(false);
                            }}
                            className="px-4 py-2.5 text-sm cursor-pointer hover:bg-blue-50 text-gray-700 transition-colors"
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Date Range */}
              {showDateRanges && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date:
                    </label>
                    <input
                      onChange={(e) => setStartDate(e.target.value)}
                      type="date"
                      value={startDate || ""}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date:
                    </label>
                    <input
                      onChange={(e) => setEndDate(e.target.value)}
                      type="date"
                      value={endDate || ""}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                    />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                {loading ? (
                  <div className="bg-blue-400 flex items-center gap-2 px-6 py-2.5 rounded-lg text-white">
                    <RotatingLines
                      visible={true}
                      height="16"
                      width="16"
                      color="black"
                      strokeColor="white"
                      strokeWidth="5"
                      animationDuration="0.75"
                      ariaLabel="rotating-lines-loading"
                      wrapperStyle={{}}
                      wrapperClass=""
                    />
                    Submitting
                  </div>
                ) : (
                  <button
                    onClick={() => getBranchExpenses()}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm"
                  >
                    Submit
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseTransaction;
